import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { money, days } from '../../api/payroll';

const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve',
  'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function under1000(n) {
  const h = Math.floor(n / 100);
  const r = n % 100;
  const parts = [];
  if (h) parts.push(`${ONES[h]} Hundred`);
  if (r) parts.push(r < 20 ? ONES[r] : `${TENS[Math.floor(r / 10)]}${r % 10 ? ` ${ONES[r % 10]}` : ''}`);
  return parts.join(' ');
}

/** 123456 -> "One Lakh Twenty Three Thousand Four Hundred Fifty Six", the way Indian payslips write it. */
export function inWords(amount) {
  let n = Math.round(Math.abs(Number(amount) || 0));
  if (n === 0) return 'Zero';
  const parts = [];
  const crore = Math.floor(n / 10000000); n %= 10000000;
  const lakh = Math.floor(n / 100000); n %= 100000;
  const thousand = Math.floor(n / 1000); n %= 1000;
  if (crore) parts.push(`${under1000(crore)} Crore`);
  if (lakh) parts.push(`${under1000(lakh)} Lakh`);
  if (thousand) parts.push(`${under1000(thousand)} Thousand`);
  if (n) parts.push(under1000(n));
  return parts.join(' ');
}

function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

const cell = { fontSize: 13, py: 0.6 };
const head = { fontSize: 11.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#5f6b7a' };

function Table({ title, rows, total, totalLabel }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0, border: '1px solid #e2e7ee', borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ px: 1.5, py: 1, bgcolor: '#f7f9fb', borderBottom: '1px solid #e2e7ee' }}>
        <Typography sx={head}>{title}</Typography>
      </Box>
      <Box sx={{ px: 1.5, py: 0.5 }}>
        {rows.length === 0 && <Typography sx={{ ...cell, color: 'text.secondary' }}>None</Typography>}
        {rows.map((l, i) => (
          <Stack key={i} direction="row" sx={{ justifyContent: 'space-between', gap: 1 }}>
            <Typography sx={cell}>
              {l.label}
              {l.note && <Box component="span" sx={{ color: 'text.secondary', fontSize: 12 }}> ({l.note})</Box>}
            </Typography>
            <Typography sx={{ ...cell, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{money(l.amount)}</Typography>
          </Stack>
        ))}
      </Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', px: 1.5, py: 1, borderTop: '1px solid #e2e7ee', bgcolor: '#fbfcfd' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{totalLabel}</Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{money(total)}</Typography>
      </Stack>
    </Box>
  );
}

/**
 * A payslip laid out the way it prints. Used for the employee's own copy and
 * for HR's preview, so both see exactly the same thing.
 */
export default function PayslipView({ slip, company }) {
  const lines = slip.lines || [];
  const earnings = lines.filter((l) => l.kind === 'EARNING');
  const deductions = lines.filter((l) => l.kind === 'DEDUCTION');
  const employer = lines.filter((l) => l.kind === 'EMPLOYER');
  const hourly = slip.payBasis === 'HOURLY';
  const lopParts = [
    slip.lopUnpaidLeave ? `${days(slip.lopUnpaidLeave)} unpaid leave` : null,
    slip.lopNegativeBalance ? `${days(slip.lopNegativeBalance)} leave beyond balance` : null,
    slip.lopAbsent ? `${days(slip.lopAbsent)} absent` : null,
  ].filter(Boolean);
  const details = [
    ['Employee', `${slip.name}${slip.employeeCode ? ` (${slip.employeeCode})` : ''}`],
    ['Designation', slip.designation],
    ['Department', slip.department],
    ['Date of joining', fmtDate(slip.dateOfJoining)],
    ['PAN', slip.pan],
    ['UAN', slip.uan],
    slip.esiNumber ? ['ESI number', slip.esiNumber] : null,
    ['Paid by', slip.paymentMode === 'BANK' ? [slip.bankName, slip.bankAccount].filter(Boolean).join(' · ') || 'Bank' : (slip.paymentMode || '').toLowerCase()],
  ].filter(Boolean);
  const ytd = slip.yearToDate;

  return (
    <Box sx={{ color: '#16202e' }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, pb: 1.5, borderBottom: '2px solid #123871' }}>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 750, color: '#123871' }}>{(company && company.name) || 'Cloudmyle'}</Typography>
          {company && company.address && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'pre-line', maxWidth: 360 }}>{company.address}</Typography>
          )}
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>Payslip</Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 700 }}>{slip.periodLabel}</Typography>
        </Box>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3, rowGap: 0.5, py: 1.5 }}>
        {details.map(([k, v]) => (
          <Stack key={k} direction="row" spacing={1} sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', minWidth: 110 }}>{k}</Typography>
            <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 600 }}>{v || '—'}</Typography>
          </Stack>
        ))}
      </Box>

      <Stack direction="row" spacing={3} sx={{ py: 1.25, px: 1.5, mb: 1.5, bgcolor: '#f5f7fa', borderRadius: 2, flexWrap: 'wrap', rowGap: 1 }}>
        {hourly ? (
          <Box><Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>Approved hours</Typography>
            <Typography sx={{ fontWeight: 700 }}>{days(slip.approvedHours)}</Typography></Box>
        ) : (
          <>
            <Box><Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>Days in month</Typography>
              <Typography sx={{ fontWeight: 700 }}>{days(slip.divisorDays)}</Typography></Box>
            <Box><Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>Paid days</Typography>
              <Typography sx={{ fontWeight: 700 }}>{days(slip.paidDays)}</Typography></Box>
            <Box><Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>Loss of pay</Typography>
              <Typography sx={{ fontWeight: 700 }}>
                {days(slip.lopDays)}
                {lopParts.length > 0 && <Box component="span" sx={{ fontWeight: 400, fontSize: 12, color: 'text.secondary' }}> · {lopParts.join(', ')}</Box>}
              </Typography></Box>
          </>
        )}
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <Table title="Earnings" rows={earnings} total={slip.gross} totalLabel="Gross earnings" />
        <Table title="Deductions" rows={deductions} total={slip.deductions} totalLabel="Total deductions" />
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ mt: 1.5, p: 1.75, borderRadius: 2, bgcolor: '#123871', color: '#fff', justifyContent: 'space-between', alignItems: { sm: 'center' }, gap: 1 }}>
        <Box>
          <Typography sx={{ fontSize: 12, opacity: 0.85, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>Net pay</Typography>
          <Typography sx={{ fontSize: 12.5, opacity: 0.9 }}>Rupees {inWords(slip.net)} only</Typography>
        </Box>
        <Typography sx={{ fontSize: 24, fontWeight: 750, fontVariantNumeric: 'tabular-nums' }}>{money(slip.net)}</Typography>
      </Stack>

      {employer.length > 0 && (
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1.25 }}>
          Paid by the company on top of this, towards your benefits: {employer.map((l) => `${l.label} ${money(l.amount)}`).join(' · ')}.
        </Typography>
      )}

      {ytd && ytd.months > 0 && (
        <Box sx={{ mt: 1.5, pt: 1.25, borderTop: '1px dashed #cfd6e1' }}>
          <Typography sx={{ ...head, mb: 0.5 }}>{ytd.financialYear} so far ({ytd.months} month{ytd.months > 1 ? 's' : ''})</Typography>
          <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
            <Typography sx={{ fontSize: 12.5 }}>Gross <b>{money(ytd.gross)}</b></Typography>
            <Typography sx={{ fontSize: 12.5 }}>Income tax <b>{money(ytd.tds)}</b></Typography>
            <Typography sx={{ fontSize: 12.5 }}>PF <b>{money(ytd.pf)}</b></Typography>
            <Typography sx={{ fontSize: 12.5 }}>Net <b>{money(ytd.net)}</b></Typography>
          </Stack>
        </Box>
      )}

      {slip.remarks && (
        <Typography sx={{ fontSize: 12.5, mt: 1.5 }}><b>Note:</b> {slip.remarks}</Typography>
      )}
      <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 2 }}>
        This is a computer-generated payslip and needs no signature.
      </Typography>
    </Box>
  );
}
